import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Avatar,
  Tag,
  Statistic,
  Timeline,
  Empty,
  Modal,
  Spin,
  Alert,
  Divider,
  notification,
  Progress,
} from 'antd';
import {
  ClockCircleOutlined,
  LoginOutlined,
  LogoutOutlined,
  UserOutlined,
  CalendarOutlined,
  HistoryOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  BellOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/lo';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from 'react-router-dom';
import { attendanceService, type Attendance } from '../services/attendanceService';
import { useAuthStore } from '../store/authStore';

dayjs.extend(relativeTime);
dayjs.locale('lo');

const { Header, Content } = Layout;
const { Text } = Typography;

const EmployeeAttendancePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [hasShownCheckoutReminder, setHasShownCheckoutReminder] = useState(false);
  const [notificationKey, setNotificationKey] = useState<string>('');

  // ==================== STATE ====================
  const [pageLoading, setPageLoading] = useState(true);

  // ອັບເດດເວລາປະຈຸບັນທຸກວິນາທີ
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // ==================== QUERIES ====================

  // ດຶງຂໍ້ມູນວັນນີ້
  const { 
    data: todayData, 
    isLoading: todayLoading, 
    refetch: refetchToday,
    error: todayError,
  } = useQuery(
    'todayAttendance',
    () => attendanceService.getTodayAttendance(),
    {
      refetchInterval: 30000, // Refresh ທຸກ 30 ວິນາທີ
      retry: 3,
      retryDelay: 1000,
    }
  );

  // ດຶງປະຫວັດເດືອນນີ້
  const { 
    data: historyData, 
    isLoading: historyLoading,
    error: historyError,
  } = useQuery(
    'thisMonthHistory',
    () => attendanceService.getThisMonthHistory(),
    {
      retry: 2,
    }
  );

  // ດຶງສະຖິຕິເດືອນນີ້
  const { 
    data: statsData, 
    isLoading: statsLoading,
    error: statsError,
  } = useQuery(
    'currentMonthStats',
    () => attendanceService.getCurrentMonthStats(),
    {
      retry: 2,
    }
  );

  // ==================== MUTATIONS ====================

  // Check-in
  const checkInMutation = useMutation(() => attendanceService.checkIn(), {
    onSuccess: () => {
      // ✅ Success Notification
      notification.success({
        message: '✅ ບັນທຶກເວລາເຂົ້າວຽກສຳເລັດ',
        description: (
          <div>
            <p>ເວລາເຂົ້າວຽກ: <strong>{dayjs().format('HH:mm:ss')}</strong></p>
            <p>ວັນທີ່: {dayjs().format('DD/MM/YYYY')}</p>
          </div>
        ),
        placement: 'topRight',
        duration: 5,
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      });

      refetchToday();
      queryClient.invalidateQueries(['thisMonthHistory']);
      queryClient.invalidateQueries(['currentMonthStats']);
    },
    onError: (error: any) => {
      // ❌ Error Notification
      notification.error({
        message: '❌ ເກີດຂໍ້ຜິດພາດ',
        description: error.response?.data?.message || 'ບໍ່ສາມາດບັນທຶກເວລາເຂົ້າວຽກໄດ້',
        placement: 'topRight',
        duration: 5,
      });
    },
  });

  // Check-out
// Check-out
const checkOutMutation = useMutation(() => attendanceService.checkOut(), {
  onSuccess: (response) => {
    const attendance = response.data;
    
    // ✅ Success Notification ພ້ອມສະຫຼຸບ
    notification.success({
      message: '✅ ບັນທຶກເວລາເລີກວຽກສຳເລັດ',
      description: (
        <div>
          <p>ເວລາເລີກວຽກ: <strong>{dayjs().format('HH:mm:ss')}</strong></p>
          <p>ຊົ່ວໂມງເຮັດວຽກ: <strong>{attendance?.actualWorkHours?.toFixed(2)} ຊົ່ວໂມງ</strong></p>
          <p style={{ color: '#52c41a', marginTop: 8 }}>
            🎉 ເຮັດວຽກໜັກມາທັງວັນ! ພັກຜ່ອນດີດີນະ
          </p>
        </div>
      ),
      placement: 'topRight',
      duration: 8,
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
    });

    // ✅ ປິດ Reminder Notification ຖ້າມີ
    if (notificationKey) {
      notification.destroy(notificationKey); // ✅ ໃຊ້ destroy ແທນ close
    }

    refetchToday();
    queryClient.invalidateQueries(['thisMonthHistory']);
    queryClient.invalidateQueries(['currentMonthStats']);
  },
  onError: (error: any) => {
    notification.error({
      message: '❌ ເກີດຂໍ້ຜິດພາດ',
      description: error.response?.data?.message || 'ບໍ່ສາມາດບັນທຶກເວລາເລີກວຽກໄດ້',
      placement: 'topRight',
      duration: 5,
    });
  },
});

  // ==================== HANDLERS ====================

  const handleCheckIn = () => {
  const now = dayjs();
  const expectedTime = user?.startWorkTime 
    ? dayjs(user.startWorkTime, 'HH:mm')
    : dayjs().hour(9).minute(0);
  
  // ✅ 1. ตรวจสอบช่วงเวลาที่อนุญาต (06:00 - 12:00)
  const earliestAllowedTime = dayjs().hour(6).minute(0);
  const latestAllowedTime = dayjs().hour(12).minute(0);
  
  if (now.isBefore(earliestAllowedTime) || now.isAfter(latestAllowedTime)) {
    notification.error({
      message: '❌ ບໍ່ສາມາດ Check-in ໄດ້',
      description: `ກະລຸນາ Check-in ລະຫວ່າງ ${earliestAllowedTime.format('HH:mm')} - ${latestAllowedTime.format('HH:mm')}`,
      placement: 'topRight',
      duration: 8,
    });
    return;
  }

  // ✅ 2. ตรวจสอบว่า Check-in เร็วเกินไป (มากกว่า 30 นาทีก่อนเวลาทำงาน)
  const earliestCheckInTime = expectedTime.subtract(30, 'minute');
  
  if (now.isBefore(earliestCheckInTime)) {
    const minutesToWait = earliestCheckInTime.diff(now, 'minute');
    
    Modal.error({
      title: '❌ Check-in ເຮັດໄວເກີນໄປ',
      content: (
        <div>
          <Alert
            message="ທ່ານກຳລັງພະຍາຍາມ Check-in ກ່ອນເວລາອັນອະນຸຍາດ"
            description={
              <div>
                <p>ເວລາປະຈຸບັນ: <strong>{now.format('HH:mm')}</strong></p>
                <p>ເວລາທີ່ສາມາດ Check-in ໄດ້ເລ່ວສຸດ: <strong>{earliestCheckInTime.format('HH:mm')}</strong></p>
                <p>ກະລຸນາລໍຖ້າອີກ: <strong style={{ color: '#ff4d4f' }}>{minutesToWait} ນາທີ</strong></p>
                <Divider />
                <p style={{ fontSize: '12px', color: '#666' }}>
                  💡 ທ່ານສາມາດ Check-in ໄດ້ພຽງ 30 ນາທີ ກ່ອນເວລາເຂົ້າວຽກເທົ່ານັ້ນ
                </p>
              </div>
            }
            type="error"
            showIcon
          />
        </div>
      ),
      okText: 'ເຂົ້າໃຈແລ້ວ',
    });
    return;
  }

  // ✅ 3. ຖ້າຜ່ານທຸກເງື່ອນໄຂ ໃຫ້ສະແດງ Confirm Dialog
  const isLate = now.isAfter(expectedTime);
  const lateMinutes = isLate ? now.diff(expectedTime, 'minute') : 0;
  const earlyMinutes = now.isBefore(expectedTime) ? expectedTime.diff(now, 'minute') : 0;

  Modal.confirm({
    title: 'ຢືນຢັນການເຂົ້າວຽກ',
    content: (
      <div>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">ເວລາປະຈຸບັນ:</Text>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
              {currentTime.format('HH:mm:ss')}
            </div>
          </div>
          
          <Divider style={{ margin: '8px 0' }} />
          
          <div>
            <Text type="secondary">ເວລາເຂົ້າວຽກກຳນົດ:</Text>
            <div style={{ fontSize: '18px', fontWeight: 500 }}>
              {expectedTime.format('HH:mm')}
            </div>
          </div>

          {isLate && (
            <Alert
              message={`⚠️ ທ່ານມາສາຍ ${lateMinutes} ນາທີ`}
              type="warning"
              showIcon
            />
          )}

          {earlyMinutes > 0 && earlyMinutes <= 30 && (
            <Alert
              message={`✅ ທ່ານມາວຽກກ່ອນເວລາ ${earlyMinutes} ນາທີ`}
              type="success"
              showIcon
            />
          )}
        </Space>
      </div>
    ),
    okText: 'ຢືນຢັນ Check-in',
    cancelText: 'ຍົກເລີກ',
    icon: <ClockCircleOutlined style={{ color: '#1890ff' }} />,
    onOk: () => checkInMutation.mutate(),
    okButtonProps: {
      loading: checkInMutation.isLoading,
    },
  });
};













  const handleCheckOut = () => {
    Modal.confirm({
      title: 'ຢືນຢັນການເລີກວຽກ',
      content: (
        <div>
          <p>ເວລາປະຈຸບັນ: <strong>{currentTime.format('HH:mm:ss')}</strong></p>
          <p style={{ marginTop: 8 }}>ທ່ານຕ້ອງການບັນທຶກເວລາເລີກວຽກບໍ່?</p>
        </div>
      ),
      okText: 'ຢືນຢັນ',
      cancelText: 'ຍົກເລີກ',
      onOk: () => checkOutMutation.mutate(),
      okButtonProps: {
        loading: checkOutMutation.isLoading,
      },
    });
  };

  const handleLogout = () => {
    Modal.confirm({
      title: 'ອອກຈາກລະບົບ',
      content: 'ທ່ານຕ້ອງການອອກຈາກລະບົບບໍ່?',
      okText: 'ອອກຈາກລະບົບ',
      okType: 'danger',
      cancelText: 'ຍົກເລີກ',
      onOk: () => {
        notification.info({
          message: '👋 ອອກຈາກລະບົບສຳເລັດ',
          description: 'ພົບກັນໃໝ່ວັນພຸ່ງນີ້!',
          placement: 'topRight',
          duration: 3,
        });
        
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 500);
      },
    });
  };

  // ==================== DATA ====================

  const todayAttendance = todayData?.data;
  const history = historyData?.data?.attendances || [];
  const stats = statsData?.data?.stats;

  const hasCheckedIn = !!todayAttendance?.checkInTime;
  const hasCheckedOut = !!todayAttendance?.checkOutTime;

  // ຄຳນວນຊົ່ວໂມງເຮັດວຽກປະຈຸບັນ (ຖ້າຍັງບໍ່ Check-out)
  const currentWorkHours =
    hasCheckedIn && !hasCheckedOut
      ? Math.max(0, (currentTime.diff(dayjs(todayAttendance.checkInTime), 'minute') - 60) / 60)
      : todayAttendance?.actualWorkHours || 0;

  // ==================== AUTO CHECK-OUT REMINDER ====================

  useEffect(() => {
  if (!user?.endWorkTime || !hasCheckedIn || hasCheckedOut || hasShownCheckoutReminder) {
    return;
  }

  const checkOutTime = dayjs(user.endWorkTime, 'HH:mm');
  const now = dayjs();
  
  // ເຊັກວ່າຜ່ານເວລາເລີກວຽກແລ້ວບໍ່ (ຫຼາຍກວ່າ 10 ນາທີ)
  const minutesAfterCheckout = now.diff(checkOutTime, 'minute');
  
  if (minutesAfterCheckout >= 10 && minutesAfterCheckout <= 180) { // 10 ນາທີ ຫາ 3 ຊົ່ວໂມງ
    const key = `checkout-reminder-${Date.now()}`;
    setNotificationKey(key);
    
    notification.warning({
      key, // ✅ ໃຊ້ key ແທນ
      message: '⏰ ແຈ້ງເຕືອນບັນທຶກເວລາເລີກວຽກ',
      description: (
        <div>
          <p>
            ເວລາເລີກວຽກປົກກະຕິ: <strong>{checkOutTime.format('HH:mm')}</strong>
          </p>
          <p>
            ຜ່ານມາແລ້ວ: <strong>{minutesAfterCheckout} ນາທີ</strong>
          </p>
          <p style={{ color: '#ff4d4f', marginTop: 8 }}>
            ⚠️ ກະລຸນາບັນທຶກເວລາເລີກວຽກ
          </p>
          <Button
            type="primary"
            danger
            size="small"
            icon={<LogoutOutlined />}
            onClick={() => {
              // ✅ ໃຊ້ notification.destroy ແທນ notification.close
              notification.destroy(key);
              handleCheckOut();
            }}
            style={{ marginTop: 8 }}
          >
            ບັນທຶກເລີກວຽກດຽວນີ້
          </Button>
        </div>
      ),
      placement: 'topRight',
      duration: 0, // ບໍ່ປິດອັດຕະໂນມັດ
      icon: <BellOutlined style={{ color: '#faad14' }} />,
      onClose: () => setHasShownCheckoutReminder(true),
    });
    
    setHasShownCheckoutReminder(true);
  }
}, [currentTime, user, hasCheckedIn, hasCheckedOut, hasShownCheckoutReminder]);

  // ==================== LATE CHECK-IN WARNING ====================

  useEffect(() => {
    if (hasCheckedIn && todayAttendance?.lateMinutes && todayAttendance.lateMinutes > 0) {
      // ສະແດງ Warning ຄັ້ງດຽວເມື່ອມາສາຍ
      const hasShownWarning = sessionStorage.getItem('late-warning-shown');
      
      if (!hasShownWarning) {
        notification.warning({
          message: '⚠️ ທ່ານມາສາຍ',
          description: (
            <div>
              <p>
                ທ່ານມາສາຍ: <strong>{attendanceService.formatLateTime(todayAttendance.lateMinutes)}</strong>
              </p>
              <p style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                ກະລຸນາມາວຽກໃຫ້ຕົງເວລາໃນຄັ້ງຕໍ່ໄປນະ
              </p>
            </div>
          ),
          placement: 'topRight',
          duration: 8,
          icon: <WarningOutlined style={{ color: '#faad14' }} />,
        });
        
        sessionStorage.setItem('late-warning-shown', 'true');
      }
    }
  }, [hasCheckedIn, todayAttendance]);

  // ==================== INITIAL LOADING ====================

  useEffect(() => {
    if (!todayLoading && !historyLoading && !statsLoading) {
      setPageLoading(false);
    }
  }, [todayLoading, historyLoading, statsLoading]);

  // ==================== ERROR HANDLING ====================

  useEffect(() => {
    if (todayError || historyError || statsError) {
      notification.error({
        message: '❌ ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ',
        description: 'ກະລຸນາລອງໃໝ່ອີກຄັ້ງ ຫຼື ຕິດຕໍ່ຜູ້ດູແລລະບົບ',
        placement: 'topRight',
        duration: 5,
      });
    }
  }, [todayError, historyError, statsError]);

  // ==================== PROGRESS BAR (ຊົ່ວໂມງເຮັດວຽກ) ====================

  const workProgressPercent = user?.workHoursPerDay 
    ? Math.min((currentWorkHours / user.workHoursPerDay) * 100, 100)
    : 0;

  // ==================== LOADING STATE ====================

  if (pageLoading) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
          }}
        >
          <Spin 
            size="large" 
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
          />
          <p style={{ marginTop: 16, fontSize: '16px', color: '#666' }}>
            ກຳລັງໂຫຼດຂໍ້ມູນ...
          </p>
        </div>
      </Layout>
    );
  }

  // ==================== RENDER ====================

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* ==================== HEADER ==================== */}
      <Header
        style={{
          background: '#fff',
          padding: '0 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Space>
            <Avatar size={40} icon={<UserOutlined />} />
            <div style={{lineHeight:'20px'}}>
              <div style={{ fontWeight: 500, fontSize: '16px' }}>
                {user?.name}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {user?.employeeId} • {user?.role}
              </div>
            </div>
          </Space>

          <Button danger icon={<PoweroffOutlined />} onClick={handleLogout} />
      
        </div>
      </Header>

      {/* ==================== CONTENT ==================== */}
      <Content style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        {/* ເວລາປະຈຸບັນ */}
        <Card
          style={{
            marginBottom: 24,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textAlign: 'center',
          }}
          loading={pageLoading}
        >
          <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: 8 }}>
            {currentTime.format('HH:mm:ss')}
          </div>
          <div style={{ fontSize: '18px', opacity: 0.9 }}>
            {currentTime.format('dddd, DD MMMM YYYY')}
          </div>
        </Card>

        {/* ປຸ່ມ Check-in / Check-out */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Button
                type="primary"
                size="large"
                icon={checkInMutation.isLoading ? <LoadingOutlined /> : <LoginOutlined />}
                onClick={handleCheckIn}
                disabled={hasCheckedIn}
                loading={checkInMutation.isLoading}
                block
                style={{
                  height: '60px',
                  fontSize: '18px',
                  background: hasCheckedIn ? undefined : '#52c41a',
                  borderColor: hasCheckedIn ? undefined : '#52c41a',
                }}
              >
                {checkInMutation.isLoading 
                  ? 'ກຳລັງບັນທຶກ...' 
                  : hasCheckedIn 
                  ? 'ບັນທຶກເວລາເຂົ້າແລ້ວ' 
                  : 'ເຂົ້າວຽກ'
                }
              </Button>
            </Col>
            <Col xs={24} sm={12}>
              <Button
                type="primary"
                size="large"
                danger
                icon={checkOutMutation.isLoading ? <LoadingOutlined /> : <LogoutOutlined />}
                onClick={handleCheckOut}
                disabled={!hasCheckedIn || hasCheckedOut}
                loading={checkOutMutation.isLoading}
                block
                style={{
                  height: '60px',
                  fontSize: '18px',
                }}
              >
                {checkOutMutation.isLoading 
                  ? 'ກຳລັງບັນທຶກ...' 
                  : hasCheckedOut 
                  ? 'ບັນທຶກເວລາອອກແລ້ວ' 
                  : 'ເລີກວຽກ'
                }
              </Button>
            </Col>
          </Row>
        </Card>

        {/* ສະຖານະວັນນີ້ */}
        {todayLoading ? (
          <Card>
            <Spin tip="ກຳລັງໂຫຼດຂໍ້ມູນວັນນີ້..." />
          </Card>
        ) : todayAttendance ? (
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                <span>ສະຖານະວັນນີ້</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Row gutter={16}>
              <Col xs={12} sm={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="ເວລາເຂົ້າວຽກ"
                    value={dayjs(todayAttendance.checkInTime).format('HH:mm')}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="ເວລາເລີກວຽກ"
                    value={
                      todayAttendance.checkOutTime
                        ? dayjs(todayAttendance.checkOutTime).format('HH:mm')
                        : '-'
                    }
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#ff4d4f', fontSize: '24px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="ຊົ່ວໂມງທຳງານ"
                    value={currentWorkHours.toFixed(2)}
                    suffix="ຊົ່ວໂມງ"
                    valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Progress Bar */}
            {hasCheckedIn && !hasCheckedOut && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">ຄວາມຄືບໜ້າການເຮັດວຽກ:</Text>
                <Progress
                  percent={Math.round(workProgressPercent)}
                  status={workProgressPercent >= 100 ? 'success' : 'active'}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                  format={() => `${currentWorkHours.toFixed(1)} / ${user?.workHoursPerDay || 8} ຊົ່ວໂມງ`}
                />
              </div>
            )}

            <Divider />

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text type="secondary">ສະຖານະ:</Text>
                  <Tag
                    color={attendanceService.getStatusColor(todayAttendance.status)}
                    style={{ fontSize: '14px', padding: '4px 12px' }}
                  >
                    {attendanceService.formatStatus(todayAttendance.status)}
                  </Tag>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text type="secondary">ເວລາສາຍ:</Text>
                  {todayAttendance.lateMinutes > 0 ? (
                    <Tag
                      color="warning"
                      icon={<WarningOutlined />}
                      style={{ fontSize: '14px', padding: '4px 12px' }}
                    >
                      {attendanceService.formatLateTime(todayAttendance.lateMinutes)}
                    </Tag>
                  ) : (
                    <Tag
                      color="success"
                      icon={<CheckCircleOutlined />}
                      style={{ fontSize: '14px', padding: '4px 12px' }}
                    >
                      ບໍ່ສາຍ
                    </Tag>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        ) : (
          <Alert
            message="ຍັງບໍ່ໄດ້ບັນທຶກເວລາເຂົ້າວຽກວັນນີ້"
            description="ກະລຸນາກົດປຸ່ມ 'ເຂົ້າວຽກ' ເພື່ອບັນທຶກເວລາ"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {/* ສະຖິຕິເດືອນນີ້ */}
        <Card
          title={
            <Space>
              <CalendarOutlined />
              <span>ສະຖິຕິເດືອນນີ້</span>
            </Space>
          }
          loading={statsLoading}
          style={{ marginBottom: 24 }}
        >
          {stats ? (
            <Row gutter={16}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="ມາວຽກ"
                  value={stats.presentDays}
                  suffix={`/ ${stats.totalDays} ວັນ`}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="ຂາດວຽກ"
                  value={stats.absentDays}
                  suffix="ວັນ"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="ມາສາຍ"
                  value={stats.lateDays}
                  suffix="ວັນ"
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="ຊົ່ວໂມງລວມ"
                  value={stats.totalWorkHours.toFixed(1)}
                  suffix="ຊົ່ວໂມງ"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
            </Row>
          ) : (
            <Empty description="ບໍ່ມີຂໍ້ມູນ" />
          )}
        </Card>

        {/* ປະຫວັດການເຂົ້າວຽກ */}
        <Card
          title={
            <Space>
              <HistoryOutlined />
              <span>ປະຫວັດການເຂົ້າວຽກ (7 ວັນຫຼ້າສຸດ)</span>
            </Space>
          }
          loading={historyLoading}
        >
          {history.length > 0 ? (
            <Timeline
              items={history.slice(0, 7).map((record: Attendance) => ({
                color: attendanceService.getStatusColor(record.status),
                children: (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 8,
                      }}
                    >
                      <Text strong>
                        {dayjs(record.date).format('DD/MM/YYYY - dddd')}
                      </Text>
                      <Tag color={attendanceService.getStatusColor(record.status)}>
                        {attendanceService.formatStatus(record.status)}
                      </Tag>
                    </div>
                    <Space split={<Divider type="vertical" />}>
                      <Text type="secondary">
                        <ClockCircleOutlined /> ເຂົ້າ:{' '}
                        {dayjs(record.checkInTime).format('HH:mm')}
                      </Text>
                      <Text type="secondary">
                        <ClockCircleOutlined /> ອອກ:{' '}
                        {record.checkOutTime
                          ? dayjs(record.checkOutTime).format('HH:mm')
                          : '-'}
                      </Text>
                      <Text type="secondary">
                        {record.actualWorkHours.toFixed(2)} ຊົ່ວໂມງ
                      </Text>
                    </Space>
                    {record.lateMinutes > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <Tag color="warning" icon={<WarningOutlined />}>
                          {attendanceService.formatLateTime(record.lateMinutes)}
                        </Tag>
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
          ) : (
            <Empty description="ບໍ່ມີປະຫວັດ" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      </Content>
    </Layout>
  );
};

export default EmployeeAttendancePage;